import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, type ITableColumn, type IRowWithDisplay } from '@/components/table/table';
import type { Payment } from '@/services/payment/payment.service';

interface BillingHistoryTableProps {
  payments: Payment[];
  isLoading?: boolean;
}

export function BillingHistoryTable({ payments, isLoading = false }: BillingHistoryTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      PENDING: { label: t('billing.table.status.pending'), className: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 hover:text-slate-800' },
      SUCCESSFUL: { label: t('billing.table.status.successful'), className: 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200 hover:text-emerald-800' },
      FAILED: { label: t('billing.table.status.failed'), className: 'bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-200 hover:text-rose-800' },
      REFUNDED: { label: t('billing.table.status.refunded'), className: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200 hover:text-amber-800' },
    };

    const statusInfo = statusMap[status.toUpperCase()] || { label: status, className: 'bg-slate-100 text-slate-700' };

    return (
      <Badge className={statusInfo.className}>
        {statusInfo.label}
      </Badge>
    );
  };

  const columns: ITableColumn[] = [
    {
      key: 'plan',
      label: t('billing.table.columns.plan'),
      width: 80,
      minWidth: 60,
      maxWidth: 120,
      resizable: true,
    },
    {
      key: 'invoice',
      label: t('billing.table.columns.invoice'),
      width: 80,
      minWidth: 60,
      maxWidth: 100,
      resizable: true,
    },
    {
      key: 'date',
      label: t('billing.table.columns.date'),
      width: 80,
      minWidth: 60,
      maxWidth: 100,
      resizable: true,
    },
    {
      key: 'status',
      label: t('billing.table.columns.status'),
      width: 60,
      minWidth: 40,
      maxWidth: 60,
      resizable: true,
    },
    {
      key: 'amount',
      label: t('billing.table.columns.amount'),
      width: 60,
      minWidth: 60,
      maxWidth: 100,
      resizable: true,
    },
    {
      key: 'actions',
      label: t('billing.table.columns.actions'),
      width: 80,
      minWidth: 70,
      maxWidth: 100,
      resizable: false,
    },
  ];

  const tableData: IRowWithDisplay[] = payments.map((payment) => {
    const timestamp = payment.createdAt 
      ? format(new Date(payment.createdAt), 'MMM dd, yyyy HH:mm')
      : '—';

    const row: any = {
      plan: payment.subscription?.plan?.displayName || '—',
      invoice: payment.invoiceNumber || '—',
      date: timestamp,
      status: payment.status,
      amount: `$${payment.amountInCents} ${payment.currency}`,
      actions: '',
    };

    row._display = {
      plan: (
        <div className="truncate whitespace-nowrap" title={payment.subscription?.plan?.displayName || '—'}>
          {payment.subscription?.plan?.displayName || '—'}
        </div>
      ),
      invoice: (
        <div className="font-mono text-sm truncate whitespace-nowrap" title={payment.invoiceNumber || '—'}>
          {payment.invoiceNumber || '—'}
        </div>
      ),
      date: (
        <div className="truncate whitespace-nowrap" title={timestamp}>
          {timestamp}
        </div>
      ),
      status: getStatusBadge(payment.status),
      amount: (
        <div className="font-semibold whitespace-nowrap">
          ${payment.amountInCents} {payment.currency}
        </div>
      ),
      actions: payment.invoiceNumber ? (
        <Button
          variant="ghost"
          size="sm"
              onClick={() => navigate(`/billing/invoice/${payment.invoiceNumber}`)}
          className="whitespace-nowrap"
        >
          <Eye className="h-4 w-4 mr-1" />
          {t('billing.table.actions.view')}
        </Button>
      ) : (
        <span className="text-slate-400">—</span>
      ),
    };

    return row as IRowWithDisplay;
  });

  return (
    <Table
      title={t('billing.table.title')}
      columns={columns}
      data={tableData}
      paginate={false}
      noHeader={false}
      isLoading={isLoading}
      showSearch={true}
    />
  );
}

